import { JobAdPage, JobAdSkill, JobAdSkillType } from '@talentmesh/job-ad';
import { AutocompleteOption } from '@talentmesh/ui/components/autocomplete-chips';
import { Button } from '@talentmesh/ui/components/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@talentmesh/ui/components/dialog';
import { cn } from '@talentmesh/ui/lib/utils';
import { ScanEyeIcon, XIcon } from 'lucide-react';
import { useJobFunctions } from '@/api/useJobFunctions';
import { siteUrls } from '@/AppSettings';
import { useCompanyContext } from '@/Context/CompanyContext';
import { JobAd } from '@/Pages/Assessments/Modify/Models/JobAd';
import { JobDetails } from '@/Pages/Assessments/Modify/Models/JobDetails';
import { getJobCategoryJobFunctionPair } from '@/Utils/JobFunctionSelectionUtils';
import UIStrings from '@/Utils/UIStrings';

interface JobAdPreviewDialogProps {
    open: boolean;
    handleClose: () => void;
    jobAd: JobAd;
    jobDetails: JobDetails;
}

function JobAdPagePreviewDialog({ open, handleClose, jobAd, jobDetails }: JobAdPreviewDialogProps) {
    const { currentCompanyProfile } = useCompanyContext();
    const { data: jobFunctionsMap } = useJobFunctions();
    const { companyName, companyIconURL } = currentCompanyProfile;

    const skills = [] as JobAdSkill[];
    const mapSkills = (input: AutocompleteOption[], type: JobAdSkillType) => {
        return input.map(
            (item) =>
                ({
                    skillName: item.label,
                    skillType: type,
                }) as JobAdSkill,
        );
    };

    if (jobAd.softSkillsSwitch) {
        skills.push(...mapSkills(jobAd.softSkills, 'SoftSkill'));
    }
    if (jobAd.hardSkillsSwitch) {
        skills.push(...mapSkills(jobAd.hardSkills, 'HardSkill'));
    }

    const previewjobAd = {
        ...jobAd,
        recruitmentId: '',
        companyName,
        companyIcon: companyIconURL,
        isAvailable: false,
        jobDetails: {
            ...jobDetails,
            jobFunctionName: getJobCategoryJobFunctionPair(jobFunctionsMap.jobCategories, jobDetails.jobFunctionId)
                .jobFunctionName,
            locationDetails: jobDetails.locationDetails,
            salary: jobDetails.doNotDisplaySalary
                ? undefined
                : {
                      yearlySalaryFrom: +jobDetails.yearlySalaryFrom, // + is used to convert string to number
                      yearlySalaryTo: +jobDetails.yearlySalaryTo,
                      currency: jobDetails.currency.id || '',
                  },
        },
        jobAdTitle: jobAd.title,
        jobAdDescription: jobAd.description,
        tests: [], // we do not show test information in preview
        minutesToComplete: 0, // we do not minutes to complete information in preview
        skills,
    };

    const settings = {
        preview: true,
        platformUrl: '', // apply button is not shown in preview
        websiteUrl: siteUrls.homePageUrl,
    };

    return (
        <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
            <DialogContent
                showCloseButton={false}
                className={cn(
                    'fixed inset-0 flex h-dvh max-h-none w-full max-w-none translate-none flex-col gap-0 rounded-none p-0',
                )}
            >
                <DialogHeader className="flex flex-row items-center justify-between gap-2 px-5 pt-5 pb-0">
                    <div className="flex flex-row items-center gap-2">
                        <ScanEyeIcon className="text-muted-foreground size-5" aria-hidden />
                        <div className="flex flex-col">
                            <DialogTitle className="text-muted-foreground text-sm font-normal">
                                {UIStrings.ThisIsAPreviewOfHowJobAdPostMayAppear}
                            </DialogTitle>
                            <DialogDescription className="text-muted-foreground">
                                {UIStrings.PleaseNoteLiveVersionMayBeDifferent}
                            </DialogDescription>
                        </div>
                    </div>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        onClick={handleClose}
                        data-cy="ClosePreviewDialogButton"
                        aria-label="close"
                        className="shrink-0"
                    >
                        <XIcon />
                        <span className="sr-only">Close</span>
                    </Button>
                </DialogHeader>
                <div className="mt-5 min-h-0 flex-1 overflow-auto pb-0">
                    <JobAdPage
                        jobAd={previewjobAd}
                        settings={{
                            ...settings,
                            preview: true,
                        }}
                    />
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default JobAdPagePreviewDialog;
